/**
 * VisualBuilderSection - Visual Configurator showcase
 *
 * Renamed from "Visual Builder" to "Visual Configurator."
 * Was a $99/mo Commerce add-on; now bundled into every paid plan.
 *
 * Layout: full-width 3D demo up top, benefit cards + pricing below.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { DEMO_LAYOUT_TYPES, DEMO_CONFIGS } from './demoConfigs';
import { DEMO_BOOKING_URL } from '../../../data/demoBooking';

const VisualBuilderDemo = dynamic(
  () => import('./VisualBuilderDemo').then((mod) => ({ default: mod.VisualBuilderDemo })),
  { ssr: false, loading: () => <DemoPlaceholder /> }
);

const StaticBuilderPreview = dynamic(
  () => import('./StaticBuilderPreview').then((mod) => ({ default: mod.StaticBuilderPreview })),
  { ssr: false, loading: () => <DemoPlaceholder /> }
);

function DemoPlaceholder() {
  return (
    <div className='w-full h-full min-h-[300px] rounded-xl bg-zinc-900/50 flex items-center justify-center'>
      <div className='animate-pulse text-zinc-500'>Loading 3D Preview...</div>
    </div>
  );
}

const formatDimension = (value) => {
  if (value === undefined || value === null) return '—';
  return `${value}"`;
};

function TabButton({ active, onClick, children, isDarkMode }) {
  return (
    <button
      type='button'
      aria-pressed={active}
      onClick={onClick}
      className={`px-3 sm:px-4 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
        active
          ? 'bg-jade-deep text-on-jade shadow-lg shadow-jade-deep/30'
          : isDarkMode
            ? 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
      }`}
    >
      {children}
    </button>
  );
}

function PartItem({ part, isDarkMode }) {
  const getDimensionString = () => {
    if (part.depth && part.depth !== part.thickness) {
      return `${formatDimension(part.width)} × ${formatDimension(part.height)} × ${formatDimension(part.depth)}`;
    }
    return `${formatDimension(part.width)} × ${formatDimension(part.height)}`;
  };

  return (
    <div
      className={`flex items-center justify-between py-2 px-3 rounded-lg ${
        isDarkMode ? 'bg-zinc-800/50' : 'bg-gray-50'
      }`}
    >
      <span className={`text-sm font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
        {part.name}
      </span>
      <span className={`text-xs font-sans ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
        {getDimensionString()}
      </span>
    </div>
  );
}

const benefits = [
  {
    title: 'Live 3D preview',
    description: 'Let buyers review the configured door, cabinet, drawer, or other product before they order.',
    icon: 'cube',
  },
  {
    title: 'Build-ready part data',
    description: 'Part dimensions and cut information come from the same product rules as the configuration.',
    icon: 'list',
  },
  {
    title: 'Custom pricing formulas',
    description: 'Pricing responds as customers change dimensions, materials, and options.',
    icon: 'dollar',
  },
  {
    title: 'Shared product rules',
    description: 'Visuals, pricing, part dimensions, and production data stay connected to one product definition.',
    icon: 'grid',
  },
];

/**
 * VisualBuilderSection Component
 *
 * @param {Object} props
 * @param {boolean} props.isDarkMode
 * @param {boolean} [props.isStatic=false]
 *   Swaps the live WebGL demo for StaticBuilderPreview. The homepage passes
 *   its performance flag here: ThreeScene already owns a WebGLRenderer there,
 *   and the demo builds a second one.
 * @param {'full'|'compact'} [props.variant='full']
 *   'full'    — /launch treatment: closes on the core commerce
 *               panel and its Calendly CTA. This is the existing behavior.
 *   'compact' — homepage treatment: same eyebrow, headline, demo and benefit
 *               cards, but the pricing panel gives way to the single jade text
 *               link every other homepage section closes on. The homepage
 *               already ends on HowItWorks + SeeInAction + a mobile sticky
 *               Book Demo, so a fourth Calendly button mid-scroll competes
 *               with its own closers rather than adding a path.
 */
export function VisualBuilderSection({
  isDarkMode,
  isStatic = false,
  variant = 'full',
}) {
  const isCompact = variant === 'compact';
  const sectionRef = useRef(null);
  const [previewReady, setPreviewReady] = useState(!isCompact);

  useEffect(() => {
    if (!isCompact || previewReady) return undefined;
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      setPreviewReady(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setPreviewReady(true);
      },
      { rootMargin: '600px 0px' }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [isCompact, previewReady]);
  const [activeLayout, setActiveLayout] = useState(DEMO_LAYOUT_TYPES.DOOR);
  const [parts, setParts] = useState([]);
  const [config, setConfig] = useState(DEMO_CONFIGS[DEMO_LAYOUT_TYPES.DOOR]);

  const [customDimensions, setCustomDimensions] = useState({
    width: DEMO_CONFIGS[DEMO_LAYOUT_TYPES.DOOR].width,
    height: DEMO_CONFIGS[DEMO_LAYOUT_TYPES.DOOR].height,
    depth: DEMO_CONFIGS[DEMO_LAYOUT_TYPES.DOOR].depth,
  });

  const handleLayoutChange = useCallback((newLayout) => {
    setActiveLayout(newLayout);
    const defaultConfig = DEMO_CONFIGS[newLayout];
    setCustomDimensions({
      width: defaultConfig.width,
      height: defaultConfig.height,
      depth: defaultConfig.depth,
    });
  }, []);

  const handleDimensionChange = useCallback((dimension, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    setCustomDimensions(prev => ({
      ...prev,
      [dimension]: numValue,
    }));
  }, []);

  const handlePartsChange = useCallback((newParts, newConfig) => {
    setParts(newParts);
    setConfig(newConfig);
  }, []);

  const layoutOptions = [
    { type: DEMO_LAYOUT_TYPES.DOOR, label: 'Door', icon: DoorIcon },
    { type: DEMO_LAYOUT_TYPES.FACE_FRAME, label: 'Face Frame', icon: FrameIcon },
    { type: DEMO_LAYOUT_TYPES.CABINET, label: 'Cabinet', icon: CabinetIcon },
    { type: DEMO_LAYOUT_TYPES.DRAWER, label: 'Drawer', icon: DrawerIcon },
    { type: DEMO_LAYOUT_TYPES.BOOKSHELF, label: 'Bookshelf', icon: BookshelfIcon },
  ];

  const is2DType = activeLayout === DEMO_LAYOUT_TYPES.DOOR || activeLayout === DEMO_LAYOUT_TYPES.FACE_FRAME;
  const showDepth = !is2DType;

  return (
    <section
      ref={sectionRef}
      id='visualConfigurator'
      className={`py-16 md:py-24 relative overflow-hidden ${
        // Compact sits between Commerce (#0a1612) and the website builder
        // (#080f0c) on the homepage, whose billboards are all green-black. The
        // neutral zinc gradient read as a grey band cutting through them, so
        // compact takes a value just off Commerce's and lets the page step
        // #0a1612 → #0c1a15 → #080f0c.
        isCompact
          ? 'bg-[#0c1a15]'
          : isDarkMode
            ? 'bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950'
            : 'bg-gradient-to-b from-gray-50 via-white to-gray-50'
      }`}
    >
      {/* Top hairline divider — the same seam-sealing rule Commerce and the
          website builder use. Without it two adjacent near-black sections
          read as one undifferentiated block. Compact only: on /launch this
          section's gradient already separates it from its neighbours. */}
      {isCompact && (
        <div
          aria-hidden='true'
          className='absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-jade-soft/30 to-transparent'
        />
      )}
      <div className={`tc-container relative z-10 ${isCompact ? 'tc-configurator-story' : ''}`}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className={isCompact ? 'tc-configurator-story__intro' : 'text-center mb-10 md:mb-14'}
        >
          {/* The configurator is part of core commerce, so no add-on badge. */}
          {isCompact ? (
            <span className='tc-eyebrow'>
              Visual Configurator
            </span>
          ) : (
            <div className='inline-flex items-center gap-2 mb-4'>
              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider ${
                  isDarkMode
                    ? 'bg-jade-deep/20 text-jade border border-jade-deep/30'
                    : 'bg-jade-deep/20 text-jade-deep border border-jade-deep/30'
                }`}
              >
                VISUAL CONFIGURATOR
              </span>
            </div>
          )}
          <h2
            className={isCompact ? 'tc-heading' : `text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4 font-display ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}
          >
            {isCompact ? 'Custom gets ' : 'Let customers '}
            <span className={isDarkMode ? 'text-jade-soft' : 'text-jade-deep'}>
              {isCompact ? 'configured,' : 'build'}
            </span>{' '}
            {isCompact ? 'not picked.' : 'what they buy'}
          </h2>
          <p
            className={isCompact ? 'tc-lead mt-5 max-w-2xl mx-auto' : `text-lg max-w-2xl mx-auto ${
              isDarkMode ? 'text-zinc-400' : 'text-gray-600'
            }`}
          >
            See it. Configure it. Price it. Then build it. Native 3D product
            configuration is part of TimberCloud commerce. Your product rules connect the preview, formula pricing, and
            part dimensions as buyers choose sizes, materials, and options.
          </p>
          {isCompact && <ul className='tc-configurator-benefits'><li><strong>Live 3D product configuration</strong><span>Let buyers see the product they configure.</span></li><li><strong>Formula-based pricing</strong><span>Price dimensions, materials, and options.</span></li><li><strong>Connected part dimensions</strong><span>Part dimensions follow the same product rules.</span></li></ul>}
          {isCompact && <a href='/product/3d-configurator' className='tc-accent inline-block mt-6 underline underline-offset-4'>See the configurator →</a>}
        </motion.div>

        {/* Full-width demo area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className={`w-full min-w-0 max-w-5xl mx-auto ${isCompact ? 'mb-6' : 'mb-12 md:mb-16'}`}
        >
          <div
            className={`rounded-2xl overflow-hidden ${
              isDarkMode
                ? 'bg-zinc-900/80 border border-zinc-800 shadow-2xl shadow-jade-deep/5'
                : 'bg-white border border-gray-200 shadow-xl'
            }`}
          >
            {/* Top bar: tabs + dimensions */}
            <div
              className={`flex flex-wrap items-center justify-between gap-3 p-4 border-b ${
                isDarkMode ? 'border-zinc-800' : 'border-gray-100'
              }`}
            >
              {/* Layout tabs */}
              <div className='flex flex-wrap gap-2' role='group' aria-label='Sample product configurations'>
                {layoutOptions.map((option) => (
                  <TabButton
                    key={option.type}
                    active={activeLayout === option.type}
                    onClick={() => handleLayoutChange(option.type)}
                    isDarkMode={isDarkMode}
                  >
                    <span className='flex items-center gap-1.5'>
                      <option.icon className='w-4 h-4' />
                      <span>{option.label}</span>
                    </span>
                  </TabButton>
                ))}
              </div>

              {/* Inline dimensions */}
              {!isStatic && (
                <div className='hidden md:flex items-center gap-2'>
                  <DimensionInput
                    label='W'
                    value={customDimensions.width}
                    onChange={(val) => handleDimensionChange('width', val)}
                    isDarkMode={isDarkMode}
                    min={1}
                    max={96}
                  />
                  <span className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-gray-300'}`}>×</span>
                  <DimensionInput
                    label='H'
                    value={customDimensions.height}
                    onChange={(val) => handleDimensionChange('height', val)}
                    isDarkMode={isDarkMode}
                    min={1}
                    max={96}
                  />
                  {showDepth && (
                    <>
                      <span className={`text-xs ${isDarkMode ? 'text-zinc-600' : 'text-gray-300'}`}>×</span>
                      <DimensionInput
                        label='D'
                        value={customDimensions.depth}
                        onChange={(val) => handleDimensionChange('depth', val)}
                        isDarkMode={isDarkMode}
                        min={1}
                        max={48}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 3D Preview + cut list side panel */}
            <div className='flex flex-col lg:flex-row'>
              {/* 3D Preview */}
              <div className='flex-1 aspect-[4/3] lg:aspect-auto lg:min-h-[420px] relative'>
                <div className='w-full h-full pointer-events-none lg:pointer-events-auto'>
                  {!previewReady ? (
                    <DemoPlaceholder />
                  ) : isStatic ? (
                    <StaticBuilderPreview layoutType={activeLayout} isDarkMode={isDarkMode} />
                  ) : (
                    <VisualBuilderDemo
                      layoutType={activeLayout}
                      isDarkMode={isDarkMode}
                      onPartsChange={handlePartsChange}
                      customDimensions={customDimensions}
                    />
                  )}
                </div>
                {!isStatic && (
                  <div
                    className={`hidden lg:block absolute bottom-4 left-4 px-3 py-1.5 rounded-full text-xs ${
                      isDarkMode
                        ? 'bg-zinc-800/80 text-zinc-400'
                        : 'bg-white/80 text-gray-500 shadow-sm'
                    }`}
                  >
                    Drag to rotate • Scroll to zoom
                  </div>
                )}
              </div>

              {/* Cut list sidebar */}
              {!isStatic && (
                <div
                  className={`lg:w-72 xl:w-80 border-t lg:border-t-0 lg:border-l p-4 ${
                    isDarkMode ? 'border-zinc-800' : 'border-gray-100'
                  }`}
                >
                  <div className='flex items-center justify-between mb-3'>
                    <h4 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {config?.label || 'Cut List'}
                    </h4>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        isDarkMode
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-green-100 text-green-600'
                      }`}
                    >
                      {parts.length} parts
                    </span>
                  </div>

                  {/* Mobile dimensions (shown only on small screens) */}
                  <div className={`md:hidden grid ${showDepth ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-4`}>
                    <DimensionInputMobile
                      label='Width'
                      value={customDimensions.width}
                      onChange={(val) => handleDimensionChange('width', val)}
                      isDarkMode={isDarkMode}
                      min={1}
                      max={96}
                    />
                    <DimensionInputMobile
                      label='Height'
                      value={customDimensions.height}
                      onChange={(val) => handleDimensionChange('height', val)}
                      isDarkMode={isDarkMode}
                      min={1}
                      max={96}
                    />
                    {showDepth && (
                      <DimensionInputMobile
                        label='Depth'
                        value={customDimensions.depth}
                        onChange={(val) => handleDimensionChange('depth', val)}
                        isDarkMode={isDarkMode}
                        min={1}
                        max={48}
                      />
                    )}
                  </div>

                  <div className='space-y-1.5 max-h-[320px] overflow-y-auto'>
                    {parts.map((part, index) => (
                      <PartItem key={index} part={part} isDarkMode={isDarkMode} />
                    ))}
                  </div>

                  <p className={`text-xs mt-3 ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'}`}>
                    Try changing dimensions or switching product types
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Benefits grid — compact closes on a text link rather than a panel,
            so it needs less air beneath it. */}
        <div
          className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto ${
            isCompact ? 'hidden' : 'mb-12 md:mb-14'
          }`}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
              viewport={{ once: true }}
              className={`p-5 rounded-xl ${
                isDarkMode
                  ? 'bg-zinc-900/60 border border-zinc-800'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              <div
                className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                  isDarkMode ? 'bg-jade-deep/15' : 'bg-jade-deep/10'
                }`}
              >
                <BenefitIcon type={benefit.icon} isDarkMode={isDarkMode} />
              </div>
              <h3 className={`text-sm font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {benefit.title}
              </h3>
              <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Close. Compact (homepage) puts its "See the configurator" text link
            in the copy column, under the benefits, like every other homepage
            section; full (/launch) keeps the pricing panel and Calendly CTA. */}
        {isCompact ? null : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className='max-w-3xl mx-auto'
        >
          <div
            className={`flex flex-col sm:flex-row items-center justify-between gap-6 p-6 sm:p-8 rounded-2xl ${
              isDarkMode
                ? 'bg-gradient-to-r from-jade-deep/10 via-jade-deep/[0.04] to-jade-deep/10 border border-jade-deep/20'
                : 'bg-gradient-to-r from-jade-deep/10 via-jade-deep/[0.03] to-jade-deep/10 border border-jade-deep/30'
            }`}
          >
            <div>
              <h3
                className={`text-lg font-bold mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                The Visual Configurator
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                <span className={`font-semibold ${isDarkMode ? 'text-jade' : 'text-jade-deep'}`}>
                  Included in core commerce
                </span>{' '}
                — connected to the same product rules as pricing and orders.
              </p>
            </div>
            <a
              href={DEMO_BOOKING_URL}
              target='_blank'
              rel='noopener noreferrer'
              className={`shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                isDarkMode
                  ? 'bg-jade-deep hover:bg-jade text-on-jade shadow-lg shadow-jade-deep/30'
                  : 'bg-jade-deep hover:bg-jade-deep text-on-jade shadow-lg shadow-jade-deep/30'
              }`}
            >
              See it in action
              <svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <path d='M5 12h14m-6-6l6 6-6 6' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </a>
          </div>
        </motion.div>
        )}
      </div>
    </section>
  );
}

/**
 * Compact inline dimension input for the top bar (desktop)
 */
function DimensionInput({ label, value, onChange, isDarkMode, min = 1, max = 96 }) {
  return (
    <div className='flex items-center gap-1'>
      <span className={`text-xs font-medium ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
        {label}
      </span>
      <div className='relative'>
        <input
          autoComplete="off"
          type='number'
          value={value === '' ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={0.25}
          className={`w-16 text-center text-sm font-semibold bg-transparent rounded-md py-1 px-1
            [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none
            [-moz-appearance:textfield] focus:ring-2 focus:ring-jade-deep transition-all border
            ${isDarkMode
              ? 'text-white border-zinc-700 hover:border-zinc-600'
              : 'text-gray-900 border-gray-200 hover:border-gray-300'
            }
          `}
        />
        <span
          className={`absolute right-1 top-1/2 -translate-y-1/2 text-xs pointer-events-none ${
            isDarkMode ? 'text-zinc-600' : 'text-gray-400'
          }`}
        >
          &quot;
        </span>
      </div>
    </div>
  );
}

/**
 * Full dimension input for mobile (in cut list sidebar)
 */
function DimensionInputMobile({ label, value, onChange, isDarkMode, min = 1, max = 96 }) {
  return (
    <div
      className={`p-2.5 rounded-lg text-center ${
        isDarkMode ? 'bg-zinc-800' : 'bg-gray-50'
      }`}
    >
      <label
        className={`block text-xs font-medium mb-1 ${
          isDarkMode ? 'text-zinc-500' : 'text-gray-400'
        }`}
      >
        {label}
      </label>
      <div className='relative'>
        <input
          autoComplete="off"
          type='number'
          value={value === '' ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={0.25}
          className={`w-full text-center text-base font-bold bg-transparent border-none outline-none appearance-none
            [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none
            [-moz-appearance:textfield] focus:ring-2 focus:ring-jade-deep rounded-md transition-all
            ${isDarkMode ? 'text-white' : 'text-gray-900'}
          `}
          style={{ paddingRight: '1.25rem' }}
        />
        <span
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-sm ${
            isDarkMode ? 'text-zinc-500' : 'text-gray-400'
          }`}
        >
          &quot;
        </span>
      </div>
    </div>
  );
}

/**
 * Benefit card icon
 */
function BenefitIcon({ type, isDarkMode }) {
  const color = isDarkMode ? 'text-jade' : 'text-jade-deep';

  switch (type) {
    case 'cube':
      return (
        <svg className={`w-5 h-5 ${color}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
          <path d='M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M3.27 6.96L12 12.01l8.73-5.05' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M12 22.08V12' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      );
    case 'list':
      return (
        <svg className={`w-5 h-5 ${color}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
          <path d='M9 5h11M9 12h11M9 19h11M5 5h.01M5 12h.01M5 19h.01' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      );
    case 'dollar':
      return (
        <svg className={`w-5 h-5 ${color}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
          <path d='M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
      );
    case 'grid':
      return (
        <svg className={`w-5 h-5 ${color}`} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5'>
          <rect x='3' y='3' width='7' height='7' rx='1' />
          <rect x='14' y='3' width='7' height='7' rx='1' />
          <rect x='3' y='14' width='7' height='7' rx='1' />
          <rect x='14' y='14' width='7' height='7' rx='1' />
        </svg>
      );
    default:
      return null;
  }
}

/**
 * Product type icons
 */
function DoorIcon({ className }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <rect x='4' y='2' width='16' height='20' rx='1' />
      <rect x='7' y='5' width='10' height='14' rx='0.5' />
    </svg>
  );
}

function CabinetIcon({ className }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <path d='M4 4h16v16H4z' />
      <path d='M4 10h16' />
      <path d='M12 4v16' />
    </svg>
  );
}

function DrawerIcon({ className }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <rect x='3' y='8' width='18' height='8' rx='1' />
      <path d='M9 12h6' />
    </svg>
  );
}

function FrameIcon({ className }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <rect x='4' y='2' width='16' height='20' rx='1' />
      <rect x='7' y='5' width='10' height='14' rx='0.5' strokeDasharray='3 2' />
    </svg>
  );
}

function BookshelfIcon({ className }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
      <rect x='4' y='2' width='16' height='20' rx='1' />
      <path d='M4 8h16' />
      <path d='M4 14h16' />
    </svg>
  );
}
